import { setUser } from "App/Authentification/Store/actions";
import { selectToken, selectTokenAdmin } from "App/Authentification/Store/selectors";
import { URL_BASE } from "App/Constants";
import { setBackVisible } from "App/Header/Store/actions";
import { setGeolocationBlocked } from "App/Store/Geolocation/actions";
import { selectIsGeolocationBlocked } from "App/Store/Geolocation/selectors";
import { showError } from "App/Toast/Toast";
import { Distribution } from "Distribution/Components/Distribution";
import i18n from "i18n";
import { RESSOURCE_TERMINAL_ETAT } from "Achat/API/AchatApi";
import { RESOURCE_ACCOUNT_INFO } from "Login/API/LoginAPI";
import { RESSOURCE_TERMINALS, RESSOURCE_TERMINALS_CLIENT } from "Map/API/MapApi";
import { MapComponent } from "Map/Components/MapV2";
import {
  ICON_ACTIVE,
  ICON_HS,
  ICON_INACTIVE,
  ICON_PAIEMENT_SUR_PLACE,
  ZOOM_ADDRESS,
  ZOOM_COUNTRY
} from "Map/Constants";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { GET } from "Request/Components/RequestUtils";
import { GET_LIST_WITH_PARAMS, GET_SINGLE, useJsonRequest } from "Request/Components/useJsonRequest";
import { selectDataResourceReceived } from "Request/Store/selectors";
import "./Accueil.css";
import { BottomNavBar } from "./BottomNavBar";

// #region CONSTANTES
// Indice des TabBar
const TAB_MAP = 0;
const TAB_DISTRIBUTION = 1;
// Erreur de géolocalisation
const GEOLOCATION_BLOCKED = 1;
// #endregion

// #region FONCTION COMPOSANT
/**
 * Composant permettant d'afficher la page d'accueil
 * @category Accueil
 */
function Accueil() {
  //#region INITIALISATION
  // Récupération des props
  const location = useLocation();
  const urlParams = new URLSearchParams(window.location.search);

  // Initialisation du state
  const [mapInfo, setMapInfo] = useState({ coordinates: { lat: 46.82407949355533, lng: 2.4905130531397117 }, zoom: ZOOM_COUNTRY, showMarker: false });
  const [selectedTab, setSelectedTab] = useState(location.state ? location.state.selectedIndexTab : null);
  const [selectedIndexTab, setSelectedIndexTab] = useState(TAB_MAP);
  const [markersForMap, setMarkersForMap] = useState([]);

  // Initialisation des selecteurs
  const dataReceived = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_TERMINALS)); // Données issues de la requête
  const dataClientReceived = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_TERMINALS_CLIENT)); // Données issues de la requête
  const terminalEtatData = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_TERMINAL_ETAT)); // État du terminal (vérification avant accès achat)
  const dataUserReceived = useSelector((state) => selectDataResourceReceived(state, RESOURCE_ACCOUNT_INFO)); // Données issues de la requête
  const isGeolocationBlocked = useSelector((state) => selectIsGeolocationBlocked(state));
  const token = useSelector((state) => selectToken(state));
  const tokenAdmin = useSelector((state) => selectTokenAdmin(state));

  // Initialisation des références
  const idClient = useRef(0);
  const pendingBorneForAchat = useRef(null); // Borne en attente de vérification terminalEtat avant navigation vers achat
  const terminaux = useRef([]);
  const terminauxSelector = useRef([]);
  const timer = useRef(-1);

  // Initialisation de l'indicateur de géolocalisation bloquée
  // Le sélecteur isGeolocationBlocked n'est pas a jour dans le setTimeOut, on est obligé de le forcer
  //cf. https://upmostly.com/tutorials/settimeout-in-react-components-using-hooks
  const geolocationRef = useRef(isGeolocationBlocked);
  geolocationRef.current = isGeolocationBlocked;

  // Récupération de l'historique
  const history = useHistory();

  // Récupération du dispatch
  const dispatch = useDispatch();

  // Traduction i18n
  const { t } = useTranslation();
  //#endregion

  // #region UTILS
  const getLocation = () => {
    if ("geolocation" in navigator) {
      // Suppression des tentatives
      clearTimeout(timer.current);
      // Récupération de la position courante
      navigator.geolocation.getCurrentPosition(
        // Succès
        (position) => {
          updateMapInfo(position)
        },
        // Erreur
        (err) => { handleErrorLocation(err) }
      );
    } else {
      // Géolocalisation inaccessible
      showError(t("geolocation.geolocationNotFound"));
      // Nouvelle tentative
      timer.current = setTimeout(() => {
        getLocation();
      }, 1000);
    }
  };

  // Récupère les options de la barre de recherche
  const getSelectOptions = (list) => {
    let options = [];

    list.forEach((marker) => {
      options.push({
        active: marker.active,
        label: marker.name,
        lat: marker.location.lat,
        lng: marker.location.lng,
        value: marker.id
      })
    });

    terminauxSelector.current = options;
  };

  const getTerminalIcon = (etatTerminal, idModePaiement) => {
    if (idModePaiement === 1 || idModePaiement === 5) {
      switch (etatTerminal) {
        case 0:
          return ICON_PAIEMENT_SUR_PLACE
        case 1:
          return ICON_PAIEMENT_SUR_PLACE
        case 2:
          return ICON_HS
        default:
          break;
      }
    }
    
    switch (etatTerminal) {
      case 0:
        return ICON_INACTIVE
      case 1:
        return ICON_ACTIVE
      case 2:
        return ICON_HS
      default:
        break;
    }
  };

  // Récupère les paramètres de requête
  const getUrlParameters = () => {
    let result = [];

    const client = urlParams.get("client");
    if (client) {
      idClient.current = client;
    }

    const idClients = urlParams.get("idClients");
    if (idClients) {
      result.push(`idClient=${idClients}`);
    }

    const idTerminaux = urlParams.get("idTerminal");
    if (idTerminaux) {
      result.push(`idTerminal=${idTerminaux}`);
    }

    const idTypeControl = urlParams.get("idTypeControl");
    if (idTypeControl) {
      result.push(`idTypeControl=${idTypeControl}`);
    }

    const language = urlParams.get("lng");
    if (language) {
      i18n.changeLanguage(language);
    }
    return result;
  }

  // Mise à jour des infos de la map
  const updateMapInfo = (position) => {
    let newMapInfo = {};

    // Mise à jour des coordonnées
    newMapInfo.coordinates = {
      lat: position.coords.latitude,
      lng: position.coords.longitude
    };

    // Mise à jour du zoom
    newMapInfo.zoom = ZOOM_ADDRESS;

    // Affichage du marker
    newMapInfo.showMarker = true;

    // Mise à jour du state
    setMapInfo(newMapInfo);
  };
  // #endregion

  // #region REQUEST
  // Requête de récupération des terminaux
  const requetGetTerminals = useJsonRequest({
    command: GET,
    getMode: GET_LIST_WITH_PARAMS,
    resource: RESSOURCE_TERMINALS,
    tokenObligatory: false,
    tokenAdminObligatory: true,
    url: URL_BASE
  });

  const requetGetTerminalsClient = useJsonRequest({
    command: GET,
    getMode: GET_SINGLE,
    resource: RESSOURCE_TERMINALS_CLIENT,
    tokenObligatory: false,
    tokenAdminObligatory: true,
    url: URL_BASE
  });

  const requestGetUserInfos = useJsonRequest({
    command: GET,
    getMode: GET_SINGLE,
    resource: RESOURCE_ACCOUNT_INFO,
    url: URL_BASE,
  });

  // Vérification de l'état du terminal avant d'accéder à l'achat (GET /api/terminalEtat/{id} → { etatTerminal: 0|1 })
  const requestGetTerminalEtat = useJsonRequest({
    command: GET,
    getMode: GET_SINGLE,
    resource: RESSOURCE_TERMINAL_ETAT,
    silent: true,
    url: URL_BASE,
  });
  // #endregion

  // #region EVENTS
  // Sélection d'une borne
  const handleClickBorne = (borne) => {
    // AYB 2025.11.20 - Vérification de la connexion avant d'accéder à l'achat
    if ((!token || !token.token) || (!tokenAdmin || !tokenAdmin.tokenAdmin)) {
      // AYB 2025.11.20 - Redirection automatique vers la page de connexion avec la borne sauvegardée
      //showError(t("accueil.disconnected"));
      history.push({
        pathname: "/connexion",
        state: { 
          mapParams: location.search,
          selectedBorne: borne // AYB 2025.11.20 - Sauvegarde de la borne pour y revenir après connexion
        }
      });
      return
    }

    if (!borne.id) {
      return;
    }

    if (!borne.active) {
      showError(t("accueil.borneNotAvailable"), { useOverlay: true, title: t("accueil.mapButton") });
      return;
    }

    // Vérification en temps réel de l'état du terminal avant d'ouvrir l'achat
    pendingBorneForAchat.current = borne;
    requestGetTerminalEtat(borne.id);
  };

  // Clic sur la nav bar pour changer d'onglet
  const handleClickBottomNavBar = (index) => {
    if (!terminaux.current) {
      return;
    }

    //AYB 25.12.04 Vérification de la connexion avant d'accéder à la distribution
    if (index === TAB_DISTRIBUTION) {
      if ((!token || !token.token) || (!tokenAdmin || !tokenAdmin.tokenAdmin)) {
        //showError(t("accueil.disconnected"));
        history.push({
          pathname: "/connexion",
          state: { 
            mapParams: location.search,
            selectedIndexTab: TAB_DISTRIBUTION
          }
        });
        return;
      }
    }
    //fin AYB
    setSelectedIndexTab(index);
  }

  // Gestion de l'erreur de géolocalisation
  const handleErrorLocation = (error) => {
    if (!geolocationRef.current) {
      // Enregistrement dans le store
      dispatch(setGeolocationBlocked(true));
      if (error.code === GEOLOCATION_BLOCKED) {
        showError(t("geolocation.geolocationNotAllowed"));
      } else {
        showError(error.message, t("geolocation.noAcess"));
      }
    }

    timer.current = setTimeout(() => {
      getLocation();
    }, 1000);
  };
  // #endregion

  // #region HOOK D'EFFET
  // Synchronisation de l'onglet affiché avec location.state (ex. redirection token expiré vers onglet carte)
  useEffect(() => {
    if (location.pathname === "/" && location.state?.selectedIndexTab !== undefined) {
      setSelectedTab(location.state.selectedIndexTab);
      setSelectedIndexTab(location.state.selectedIndexTab);
    }
  }, [location.pathname, location.state]);

  // On cache le bouton back du header
  useEffect(() => {
    dispatch(setBackVisible(false));
  }, [dispatch]);

  // Réaction à la vérification d'état du terminal : navigation vers achat si disponible, sinon message d'erreur
  useEffect(() => {
    if (!pendingBorneForAchat.current || !terminalEtatData) {
      return;
    }
    const borne = pendingBorneForAchat.current;
    pendingBorneForAchat.current = null;
    const TERMINAL_DISPONIBLE = 1;
    if (terminalEtatData.etatTerminal !== TERMINAL_DISPONIBLE) {
      showError(t("achat.terminalDisconnected"));
      return;
    }
    history.push({
      pathname: "/achat",
      state: {
        borne,
        codeAcces: "",
        dateValidite: null,
        depassement: 0,
        immatriculation: "",
        rachat: false,
        rachatIdService: null,
        servicesActifs: []
      }
    });
  }, [terminalEtatData, history, t]);

  // Lancement de la requête
  useEffect(() => {
    // récupération du paramètre d'url
    let urlParams = getUrlParameters();

    // const client = null;
    // Requête des terminaux
    if (idClient.current) {
      requetGetTerminalsClient(idClient.current);
    } else {
      requetGetTerminals(urlParams);
    }

    // Récupération de la position utilisateur
    timer.current = setTimeout(() => {
      getLocation();
    }, 1000);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requetGetTerminals]);

  // Récupération des terminaux
  useEffect(() => {
    if (!dataReceived && !dataClientReceived) {
      setMarkersForMap([]);
      return;
    }

    let d = [];
    if (dataReceived) {
      d = [...dataReceived];
    } else {
      d = [...dataClientReceived];
    }

    let markerList = [];

    d.forEach(terminal => {
      // Formatage de la localisation
      let location = terminal.gpsTerminal.split(',');

      // Formatage de la resource terminal
      let newTerminal = {
        active: terminal.etatTerminal === 1 ? true : false,
        icon: getTerminalIcon(terminal.etatTerminal, terminal.idModePaiement),
        id: terminal.idTerminal,
        idModePaiement: terminal.idModePaiement,
        location: {
          lat: parseFloat(location[0]),
          lng: parseFloat(location[1])
        },
        name: terminal.nomTerminal,
        serviceList: terminal.serviceList,
        zones: terminal.zones
      }

      markerList.push(newTerminal);
    });

    if (markerList.length === 1 && ((token && token.token) && (tokenAdmin && tokenAdmin.tokenAdmin))) {
      terminaux.current = markerList;
      getSelectOptions(markerList);
      setMarkersForMap(markerList);
      handleClickBorne(markerList[0]);
      return;
    }

    // Enregistrement des terminaux formatés
    terminaux.current = markerList;
    getSelectOptions(markerList);
    setMarkersForMap(markerList);

    if (markerList && selectedTab === TAB_DISTRIBUTION) {
      setSelectedTab(TAB_MAP);
      setSelectedIndexTab(selectedTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReceived, dataClientReceived]);

  // Requête des informations utilisateurs
  useEffect(() => {
    requestGetUserInfos();
  }, [requestGetUserInfos]);

  // Réception et enregistrement des informations de l'utilisateur
  useEffect(() => {
    if (!dataUserReceived) {
      return;
    }

    let d = {};

    // La route renvoie un objet lors du PATCH
    if (Array.isArray(dataUserReceived)) {
      d = {
        ...d,
        email: dataUserReceived[0].email ?? "",
        firstName: dataUserReceived[0].firstName ?? "",
        id: dataUserReceived[0].id,
        name: dataUserReceived[0].name ?? "",
        plate: dataUserReceived[0].plate ?? "",
        username: dataUserReceived[0].username,
      }
    } else {
      d = {
        ...d,
        email: dataUserReceived.email ?? "",
        firstName: dataUserReceived.firstName ?? "",
        id: dataUserReceived.id,
        name: dataUserReceived.name ?? "",
        plate: dataUserReceived.plate ?? "",
        username: dataUserReceived.username,
      }
    }

    // Enregistrement dans le store
    const userDataToStore = {
      email: d.email,
      firstName: d.firstName,
      id: d.id,
      name: d.name,
      plate: d.plate,
      username: d.username,
    }

    dispatch(setUser(userDataToStore));
  }, [dataUserReceived, dispatch]);
  // #endregion

  // #region INTERFACE
  return (
    <div className="Accueil-Container">
      {(selectedIndexTab === TAB_MAP)
        ?
        <>
          <MapComponent
            center={mapInfo.coordinates}
            isMarkerShown={mapInfo.showMarker}
            markers={markersForMap}
            terminauxSelector={terminauxSelector.current}
            onChangeActiveTab={setSelectedIndexTab}
            onClick={(borne) => { handleClickBorne(borne) }}
            selectedIndexTab={selectedIndexTab}
            zoom={mapInfo.zoom}
          />
        </>
        :
        <Distribution borne={terminaux.current} />
      }
      <BottomNavBar
        activeTabIndex={selectedIndexTab}
        onChangeActiveTab={(index) => handleClickBottomNavBar(index)}
      />
    </div>
  );
  // #endregion
};
// #endregion

export { Accueil };

